<template>
  <div class="d-flex flex-column width-100 height-100 pa-4">

    <!-- Shared Prompt Input -->
    <v-text-field
      v-model="testPrompt"
      label="Shared Test Prompt for Gemini"
      outlined
      dense
      class="mb-4"
      hide-details="auto"
      clearable
    />

    <!-- Gemini Test Buttons & Results -->
    <v-row>
      <!-- Flash Reasoning -->
      <v-col cols="12" md="6">
        <v-card outlined class="pa-3 mb-4 test-card">
          <v-card-title class="text-subtitle-1 pb-2">Flash Reasoning</v-card-title>
          <v-btn
            @click="testFlashReasoning"
            :loading="isTestingFlashReasoning"
            :disabled="isTestingFlashReasoning || !isLoggedIn"
            color="blue lighten-2"
            block
            class="mb-2"
            small
          >
            Test Flash Reasoning
          </v-btn>
          <v-alert v-if="flashReasoningError" type="error" dense text class="mb-2 text-caption">
            {{ flashReasoningError }}
          </v-alert>
          <div v-if="flashReasoningResult" class="mt-2">
            <div class="text-overline text-caption">Response:</div>
            <pre class="response-box">{{ flashReasoningResult }}</pre>
          </div>
        </v-card>
      </v-col>

      <!-- Pro Reasoning -->
      <v-col cols="12" md="6">
        <v-card outlined class="pa-3 mb-4 test-card">
          <v-card-title class="text-subtitle-1 pb-2">Pro Reasoning</v-card-title>
          <v-btn
            @click="testProReasoning"
            :loading="isTestingProReasoning"
            :disabled="isTestingProReasoning || !isLoggedIn"
            color="deep-purple lighten-2"
            block
            class="mb-2"
            small
          >
            Test Pro Reasoning
          </v-btn>
          <v-alert v-if="proReasoningError" type="error" dense text class="mb-2 text-caption">
            {{ proReasoningError }}
          </v-alert>
          <div v-if="proReasoningResult" class="mt-2">
            <div class="text-overline text-caption">Response:</div>
            <pre class="response-box">{{ proReasoningResult }}</pre>
          </div>
        </v-card>
      </v-col>

      <!-- Flash Streaming -->
      <v-col cols="12" md="6">
        <v-card outlined class="pa-3 mb-4 test-card">
          <v-card-title class="text-subtitle-1 pb-2">Flash Streaming</v-card-title>
          <v-btn
            @click="testFlashStreaming"
            :loading="isTestingFlashStreaming"
            :disabled="isTestingFlashStreaming || !isLoggedIn"
            color="teal lighten-2"
            block
            class="mb-2"
            small
          >
            Test Flash Streaming
          </v-btn>
          <v-alert v-if="flashStreamingError" type="error" dense text class="mb-2 text-caption">
            {{ flashStreamingError }}
          </v-alert>
          <div v-if="flashStreamingResult || (isTestingFlashStreaming && !flashStreamingError)" class="mt-2">
            <div class="text-overline text-caption">Streamed Response:</div>
            <pre class="response-box">{{ flashStreamingResult }}</pre>
            <v-chip v-if="flashStreamingComplete && !flashStreamingError" small color="success" class="mt-1 text-caption">Stream Complete</v-chip>
          </div>
        </v-card>
      </v-col>

      <!-- Pro Streaming -->
      <v-col cols="12" md="6">
        <v-card outlined class="pa-3 mb-4 test-card">
          <v-card-title class="text-subtitle-1 pb-2">Pro Streaming</v-card-title>
          <v-btn
            @click="testProStreaming"
            :loading="isTestingProStreaming"
            :disabled="isTestingProStreaming || !isLoggedIn"
            color="orange lighten-2"
            block
            class="mb-2"
            small
          >
            Test Pro Streaming
          </v-btn>
          <v-alert v-if="proStreamingError" type="error" dense text class="mb-2 text-caption">
            {{ proStreamingError }}
          </v-alert>
          <div v-if="proStreamingResult || (isTestingProStreaming && !proStreamingError)" class="mt-2">
            <div class="text-overline text-caption">Streamed Response:</div>
            <pre class="response-box">{{ proStreamingResult }}</pre>
            <v-chip v-if="proStreamingComplete && !proStreamingError" small color="success" class="mt-1 text-caption">Stream Complete</v-chip>
          </div>
        </v-card>
      </v-col>
    </v-row>
    <!-- End Gemini Test Section -->

    <!-- Status/Transcription Area -->
    <v-sheet color="grey lighten-4 flex-grow-1" rounded class="pa-2 mb-4 d-flex align-center justify-center">
       <div v-if="isRecording" class="text-center">Recording... Speak now!</div>
       <div v-else-if="isProcessing" class="text-center">Processing audio...</div>
       <div v-else-if="transcriptionError" class="text-center red--text">{{ transcriptionError }}</div>
       <div v-else-if="!isLoggedIn" class="text-center grey--text">Please log in to use voice input.</div>
       <div v-else class="text-center grey--text">Press and hold the microphone to speak.</div>
    </v-sheet>

    <!-- Main Message Input & Mic Button -->
    <div class="d-flex flex-row align-center">
      <v-text-field
        v-model="message"
        outlined
        hide-details
        placeholder="Your message..."
        @keyup.enter="sendMessage"
      />
      <v-btn
        fab
        :color="isRecording ? 'red' : (isLoggedIn ? 'black' : 'grey')"
        :disabled="!isLoggedIn || isProcessing || isTestingFlashReasoning || isTestingProReasoning || isTestingFlashStreaming || isTestingProStreaming"
        dark
        elevation="0"
        small
        class="ml-2"
        @mousedown="startRecording"
        @mouseup="stopRecording"
        @touchstart.prevent="startRecording"
        @touchend.prevent="stopRecording"
        @mouseleave="cancelRecordingOnMouseLeave"
      >
        <v-icon>mdi-microphone-outline</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
// Import necessary Firebase auth functions
import { getIdToken } from "firebase/auth";
// If you are not using a global auth instance and need to get it per component:
// import { getAuth } from "firebase/auth";

export default {
  name: 'GameSettings',

  data() {
    return {
      // Existing data properties
      message: '',
      isRecording: false,
      isProcessing: false,
      transcriptionError: null,
      mediaRecorder: null,
      audioChunks: [],

      // --- Gemini Test Properties ---
      testPrompt: 'Write a short, fun poem about a curious cat exploring a garden.', // Shared prompt

      // Flash Reasoning
      isTestingFlashReasoning: false,
      flashReasoningResult: null,
      flashReasoningError: null,

      // Flash Streaming
      isTestingFlashStreaming: false,
      flashStreamingResult: '',
      flashStreamingError: null,
      flashStreamingComplete: false,

      // Pro Reasoning
      isTestingProReasoning: false,
      proReasoningResult: null,
      proReasoningError: null,

      // Pro Streaming
      isTestingProStreaming: false,
      proStreamingResult: '',
      proStreamingError: null,
      proStreamingComplete: false,
      // No need for eventSource properties if using fetch for streaming
    };
  },

  computed: {
    isLoggedIn() {
      return !!this.authUser;
    },
  },

  methods: {
    
    async requestMicrophonePermission() {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        return true;
      } catch (err) {
        console.error("Mic permission error:", err.name, err.message);
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') this.transcriptionError = "Mic permission denied.";
        else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') this.transcriptionError = "No mic found.";
        else this.transcriptionError = "Could not access mic.";
        return false;
      }
    },
    async startRecording() {
      if (this.isRecording || this.isProcessing || this.isTestingFlashReasoning || this.isTestingProReasoning || this.isTestingFlashStreaming || this.isTestingProStreaming) return;
      if (!this.isLoggedIn) { this.transcriptionError = "Please log in."; return; }
      this.transcriptionError = null;
      if (!await this.requestMicrophonePermission()) return;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const options = { mimeType: 'audio/webm; codecs=opus' };
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = 'audio/webm';
          if (!MediaRecorder.isTypeSupported(options.mimeType)) {
            this.transcriptionError = "No suitable audio format supported.";
            stream.getTracks().forEach(track => track.stop()); return;
          }
        }
        this.mediaRecorder = new MediaRecorder(stream, options);
        this.audioChunks = [];
        this.mediaRecorder.ondataavailable = (event) => event.data.size > 0 && this.audioChunks.push(event.data);
        this.mediaRecorder.onstop = async () => {
          stream.getTracks().forEach(track => track.stop());
          if (this.audioChunks.length === 0) { this.isRecording = false; return; }
          this.isProcessing = true;
          const audioBlob = new Blob(this.audioChunks, { type: this.mediaRecorder.mimeType });
          this.audioChunks = [];
          let tokenToSend;
          if (!this.authUser) { this.transcriptionError = "Session ended."; this.isProcessing = false; return; }
          try { tokenToSend = await getIdToken(this.authUser); }
          catch (error) { console.error("Token error:", error); this.transcriptionError = "Auth error."; this.isProcessing = false; return; }
          try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.webm');
            const response = await fetch(this.getFunctionsUrl('transcribeAudio'), { method: 'POST', body: formData, headers: { 'Authorization': `Bearer ${tokenToSend}` } });
            const responseBodyText = await response.text();
            if (!response.ok) {
              let errData = { error: `Server error: ${response.status}` };
              try { errData = JSON.parse(responseBodyText); } catch (e) { errData.error = responseBodyText || errData.error; }
              throw new Error(errData.error);
            }
            const result = JSON.parse(responseBodyText);
            if (result.transcript !== undefined) this.message = (this.message ? this.message + ' ' : '') + result.transcript.trim();
            else if (result.error) this.transcriptionError = result.error;
            else this.transcriptionError = "Unexpected response.";
          } catch (err) { console.error('Transcription fetch error:', err); this.transcriptionError = err.message || 'Transcription failed.'; }
          finally { this.isProcessing = false; }
        };
        this.mediaRecorder.start();
        this.isRecording = true;
      } catch (err) { console.error('startRecording setup error:', err); this.transcriptionError = 'Failed to start recording.'; }
    },
    stopRecording() {
      if (this.mediaRecorder && this.isRecording) { this.mediaRecorder.stop(); this.isRecording = false; }
    },
    cancelRecordingOnMouseLeave() {
      if (this.isRecording) {
        this.mediaRecorder.onstop = () => { if (this.mediaRecorder && this.mediaRecorder.stream) this.mediaRecorder.stream.getTracks().forEach(track => track.stop()); this.audioChunks = []; };
        this.mediaRecorder.stop(); this.isRecording = false; this.isProcessing = false; this.transcriptionError = null;
      }
    },
    sendMessage() { if (this.message.trim()) console.log('Sending (placeholder):', this.message); },

    async _testReasoningEndpoint(endpointType) {
      let isLoadingFlag, resultProp, errorProp, functionURL, friendlyName;
      if (endpointType === 'flash') { [isLoadingFlag, resultProp, errorProp, functionURL, friendlyName] = ['isTestingFlashReasoning', 'flashReasoningResult', 'flashReasoningError', this.$endpoints.reasoningGeminiFlash, 'Flash Reasoning']; }
      else if (endpointType === 'pro') { [isLoadingFlag, resultProp, errorProp, functionURL, friendlyName] = ['isTestingProReasoning', 'proReasoningResult', 'proReasoningError', this.$endpoints.reasoningGeminiPro, 'Pro Reasoning']; }
      else return;
      this[isLoadingFlag] = true; this[resultProp] = null; this[errorProp] = null;
      if (!this.isLoggedIn) { this[errorProp] = `Log in to test ${friendlyName}.`; this[isLoadingFlag] = false; return; }
      const prompt = this.testPrompt.trim();
      if (!prompt) { this[errorProp] = "Enter a prompt."; this[isLoadingFlag] = false; return; }
      let tokenToSend;
      try { if (!this.authUser) throw new Error("User not found."); tokenToSend = await getIdToken(this.authUser); }
      catch (error) { console.error(`Token error for ${friendlyName}:`, error); this[errorProp] = "Auth error."; this[isLoadingFlag] = false; return; }
      try {
        const response = await fetch(functionURL, { method: 'POST', headers: { 'Authorization': `Bearer ${tokenToSend}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ prompt }) });
        const responseBodyText = await response.text();
        if (!response.ok) {
          let errData = { error: `Request failed: ${response.status}` };
          try { errData = JSON.parse(responseBodyText); } catch (e) { errData.error = responseBodyText || errData.error; }
          throw new Error(errData.error);
        }
        const result = JSON.parse(responseBodyText);
        if (result.response !== undefined) this[resultProp] = result.response;
        else throw new Error("Invalid response: 'response' missing.");
      } catch (error) { console.error(`Error calling ${friendlyName}:`, error); this[errorProp] = error.message || 'Unknown error.'; }
      finally { this[isLoadingFlag] = false; }
    },

    async _testStreamingEndpoint(endpointType) {
      let isLoadingFlag, resultProp, errorProp, completeFlag, functionURL, friendlyName;
      if (endpointType === 'flash') { [isLoadingFlag, resultProp, errorProp, completeFlag, functionURL, friendlyName] = ['isTestingFlashStreaming', 'flashStreamingResult', 'flashStreamingError', 'flashStreamingComplete', this.$endpoints.streamingGeminiFlash, 'Flash Streaming']; }
      else if (endpointType === 'pro') { [isLoadingFlag, resultProp, errorProp, completeFlag, functionURL, friendlyName] = ['isTestingProStreaming', 'proStreamingResult', 'proStreamingError', 'proStreamingComplete', this.$endpoints.streamingGeminiPro, 'Pro Streaming']; }
      else return;
      this[isLoadingFlag] = true; this[resultProp] = ''; this[errorProp] = null; this[completeFlag] = false;
      if (!this.isLoggedIn) { this[errorProp] = `Log in to test ${friendlyName}.`; this[isLoadingFlag] = false; return; }
      const prompt = this.testPrompt.trim();
      if (!prompt) { this[errorProp] = "Enter a prompt."; this[isLoadingFlag] = false; return; }
      let tokenToSend;
      try { if (!this.authUser) throw new Error("User not found."); tokenToSend = await getIdToken(this.authUser); }
      catch (error) { console.error(`Token error for ${friendlyName}:`, error); this[errorProp] = "Auth error."; this[isLoadingFlag] = false; return; }
      try {
        const response = await fetch(functionURL, { method: 'POST', headers: { 'Authorization': `Bearer ${tokenToSend}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ prompt }) });
        if (!response.ok) {
          let errData = { error: `Request failed: ${response.status}` };
          try { const errText = await response.text(); errData = JSON.parse(errText); } catch (e) { /* ignore */ }
          throw new Error(errData.error || `Request failed: ${response.status}`);
        }
        if (!response.body) throw new Error("Response body is null.");
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) { this[completeFlag] = true; break; }
          const rawChunk = decoder.decode(value);
          const lines = rawChunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonData = JSON.parse(line.substring(6));
                if (jsonData.chunk) this[resultProp] += jsonData.chunk;
                else if (jsonData.error) this[errorProp] = (this[errorProp] ? this[errorProp] + '\n' : '') + `Stream error: ${jsonData.error.details || jsonData.error}`;
                else if (jsonData.status === 'completed' || jsonData.status === 'completed_empty') this[completeFlag] = true;
                else if (jsonData.warning) console.warn(`${friendlyName} warning:`, jsonData.warning);
              } catch (e) { console.warn(`Could not parse SSE data line: "${line}"`, e); }
            }
          }
        }
      } catch (error) { console.error(`Error during ${friendlyName} stream:`, error); this[errorProp] = (this[errorProp] ? this[errorProp] + '\n' : '') + (error.message || 'Streaming failed.'); this[completeFlag] = true; }
      finally { this[isLoadingFlag] = false; }
    },

    testFlashReasoning() { this._testReasoningEndpoint('flash'); },
    testProReasoning() { this._testReasoningEndpoint('pro'); },
    testFlashStreaming() { this._testStreamingEndpoint('flash'); },
    testProStreaming() { this._testStreamingEndpoint('pro'); },

  }, // end of methods

  beforeDestroy() {
    if (this.mediaRecorder) {
      if (this.isRecording || this.mediaRecorder.state !== 'inactive') {
        try { this.mediaRecorder.onstop = null; this.mediaRecorder.stop(); }
        catch (e) { console.warn("Error stopping MediaRecorder in destroy:", e); }
      }
      if (this.mediaRecorder.stream) this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
      this.mediaRecorder = null;
    }
    this.isRecording = false; this.isProcessing = false;
    // Close any active streaming connections if they were using EventSource (not needed for fetch-based)
    // if (this.eventSourceFlashStreaming) this.eventSourceFlashStreaming.close();
    // if (this.eventSourceProStreaming) this.eventSourceProStreaming.close();
  }
};
</script>

<style scoped>
.width-100 { width: 100%; }
.height-100 { height: 100%; }
.test-card { min-height: 250px; /* Ensure cards have some base height */ }
pre.response-box {
  background-color: #f8f9fa;
  padding: 8px 12px;
  border-radius: 4px;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
  font-size: 0.85em;
  color: #212529;
  border: 1px solid #dee2e6;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 200px; /* Adjust as needed */
  overflow-y: auto;
}
.text-caption { font-size: 0.75rem !important; } /* For Vuetify's text-caption */
.v-alert.text-caption { line-height: 1.2; } /* Improve line height for small alerts */
</style>